import React, { useCallback, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import MobileSubtitle from 'ui/MobileSubtitle/MobileSubtitle';
import Tabs from 'ui/Tabs/Tabs';
import Loader from 'ui/Loader/Loader';
import { WarningModal } from 'components/modals';
import { IAccount } from 'store/accounts.model';
import accountsStore from 'store/accountsStore';
import SingleCardLoad from './tabs/SingleCardLoad/SingleCardLoad';
import FileUpload from './tabs/BulkCardLoad/bulkTabs/FileUpload/FileUpload';
import ManualEntry from './tabs/BulkCardLoad/bulkTabs/ManualEntry/ManualEntry';

import styles from './PrepaidCard.module.css';

const tabsOptions = [{ label: 'Single Card Load' }, { label: 'Bulk Card Load' }];

const innerTabsOptions = [{ label: 'File Upload' }, { label: 'Manual Entry' }];

const PrepaidCardLoad = () => {
  const [activeTab, setActiveTab] = useState<number | false>(0);
  const [activeInnerTab, setActiveInnerTab] = useState<number | false>(0);
  const [nextTab, setNextTab] = useState<number | false>(0);
  const [nextInnerTab, setNextInnerTab] = useState<number | false>(false);
  const [loadingData, setLoadingData] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [isFormDirty, setFormDirty] = useState(false);
  const [accountsList, setAccountsList] = useState<IAccount[]>([]);
  const [bulkUploadState, setBulkUploadState] = useState<'adding' | 'confirmation' | 'report'>('adding');

  const openAddBulkUploadForm = useCallback(() => setBulkUploadState('adding'), []);
  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  const { accounts: storeAccounts } = accountsStore;

  useEffect(() => setAccountsList(storeAccounts.filter(({ currency }) => currency === 'GHS')), [storeAccounts]);
  interface IIocationState {
    tab: number | false;
    innerTab: number | false;
    data: Record<string, string>;
  }

  const location = useLocation<IIocationState>();

  useEffect(() => {
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
    }
    if (location.state) {
      setActiveTab(location.state.tab);
      setActiveInnerTab(location.state.innerTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMdScreen, location.state]);

  useEffect(() => {
    setLoadingData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchAccounts = () => {
      if (storeAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    setLoadingData(true);

    Promise.all([fetchAccounts()]).finally(() => {
      setLoadingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeTab = useCallback(
    (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
      if (tabIndex !== 1 && nextInnerTab !== false) setNextInnerTab(false);
      if (tabIndex === 1 && nextInnerTab === false) setNextInnerTab(0);
      setNextTab(tabIndex);

      if (isFormDirty) {
        setShowWarning(true);
      } else {
        if (tabIndex !== 1 && nextInnerTab !== false) setActiveInnerTab(false);
        if (tabIndex === 1 && nextInnerTab === false) setActiveInnerTab(0);
        setActiveTab(tabIndex);
      }
    },
    [isFormDirty, nextInnerTab],
  );

  const handleOnChangeInnerTab = (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
    if (nextTab !== 1) setNextTab(1);
    setNextInnerTab(tabIndex);

    if (isFormDirty) {
      setShowWarning(true);
    } else {
      if (activeTab !== 1) setActiveTab(1);
      setActiveInnerTab(tabIndex);
    }
  };

  const handleClickBack = () => {
    if (isFormDirty) {
      setShowWarning(true);
    } else {
      setActiveTab(false);
    }
  };

  const onLeavePage = () => {
    setShowWarning(false);
    setFormDirty(false);
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
    }
  };

  const actualOptions = downMdScreen && activeTab === false ? tabsOptions.slice(0, 1) : tabsOptions;
  const actualActiveInnerTab = downMdScreen && activeTab !== false ? 0 : activeInnerTab;

  const renderTabs = () => {
    if (downMdScreen && activeTab !== false) {
      return null;
    }

    return (
      <>
        <Tabs value={activeTab} options={actualOptions} onChange={handleOnChangeTab} disabled={loadingData} />
        <Tabs
          value={actualActiveInnerTab}
          options={innerTabsOptions}
          onChange={handleOnChangeInnerTab}
          theme="secondary"
          disabled={loadingData}
        />
      </>
    );
  };

  const closeWarning = () => setShowWarning(false);
  const switchDirtyTrue = () => setFormDirty(true);
  const showMainForm = !loadingData && (activeTab !== 1 || bulkUploadState === 'adding');

  return (
    <>
      <DashboardCard className={styles.dashCard}>
        <NavigationTitle
          to="/prepaidCardLoad"
          layoutType={LayoutType.dashboard}
          onClick={handleClickBack}
          noArrow={activeTab === false}
        >
          Prepaid Card Load
        </NavigationTitle>
        {downMdScreen && activeTab === false && <MobileSubtitle>Select category</MobileSubtitle>}
        {renderTabs()}
        {/* <Tabs value={activeTab} options={tabsOptions} onChange={handleOnChangeTab} theme="secondary" /> */}
        {loadingData ? <Loader /> : null}
        {!loadingData && activeTab === 0 && (
          <SingleCardLoad handleChangeForm={switchDirtyTrue} accounts={accountsList} />
        )}
      </DashboardCard>
      {!loadingData && activeInnerTab === 0 && (
        <FileUpload
          accounts={accountsList}
          bulkUploadState={bulkUploadState}
          openAddBulkUploadForm={openAddBulkUploadForm}
          showMainForm={showMainForm}
          handleChangeForm={switchDirtyTrue}
        />
      )}
      {!loadingData && activeInnerTab === 1 && (
        <ManualEntry accounts={accountsList} showMainForm={showMainForm} handleChangeForm={switchDirtyTrue} />
      )}
      <WarningModal open={showWarning} submitName="Leave Page" onSubmit={onLeavePage} onClose={closeWarning}>
        Entered data will be lost. Are you sure you want to leave page?
      </WarningModal>
    </>
  );
};

export default PrepaidCardLoad;
